import { useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { HorizontalStack, Icon, Spinner } from '@ztechapps/webui.util'
import { Collapse } from 'react-bootstrap'
import { MCP_ERROR_PREFIX } from '@ztechapps/ai.common'
import { humanize } from '@ztechapps/common.utils'
import { useInjectComponent, WebuiIocKey } from '@ztechapps/webui.contract'
import styles from './styles.module.css'

/**
 * Tool part from message parts array - can be typed (tool-{name}) or dynamic
 */
export type ToolPart = {
  type: string // 'tool-{name}' or 'dynamic-tool'
  toolCallId: string
  toolName?: string // Only present on dynamic-tool parts
  input?: unknown
  output?: unknown
  state: string
}

export type ToolCallDisplayProps = {
  /** The tool part to display (from message.parts) */
  toolInvocation: ToolPart
}

/**
 * ToolCallDisplay component shows a tool call in the chat.
 * Displays the tool name, arguments, and state (partial-call, call, or result).
 */
export const ToolCallDisplay = ({ toolInvocation }: ToolCallDisplayProps) => {
  const { type, input, output, state } = toolInvocation

  // Extract tool name: from toolName property (dynamic-tool) or from type prefix (tool-{name})
  const toolName = toolInvocation.toolName ?? (type.startsWith('tool-') ? type.replace('tool-', '') : type)

  // Collapsible state - default collapsed for completed tools
  const [isExpanded, setIsExpanded] = useState(false)

  // `chatRenderer.unknown` is the bound no-op fallback; exclude it from the
  // per-tool lookup so a tool literally named "unknown" cannot collide and so
  // the truthy-key gate below correctly treats "no renderer" as "fall through
  // to JSON view".
  const chatRendererKeys = WebuiIocKey.ai.chatRenderer as Record<string, symbol>
  const rendererKey = toolName === 'unknown' ? undefined : chatRendererKeys[toolName]
  const Renderer = useInjectComponent<Record<string, unknown>>(rendererKey ?? WebuiIocKey.ai.chatRenderer.unknown)

  // Determine the display state based on UIToolInvocation state type
  // New AI SDK v6 states: 'input-streaming', 'input-available', 'output-available', 'output-error'
  const isProcessing = state === 'input-streaming' || state === 'input-available'
  const isResult = state === 'output-available'
  // MCP tools report a failure as an "Error: …" text result (BaseMcpTool.createErrorResponse), which the
  // stream delivers as a successful `output-available` — read the prefix so the badge does not say "Completed".
  const isError =
    state === 'output-error' || (isResult && typeof output === 'string' && output.startsWith(MCP_ERROR_PREFIX))

  // Get the appropriate icon and color based on state
  const getStateIndicator = () => {
    if (state === 'input-streaming') {
      return { icon: 'spinner', color: 'text-warning', label: 'Processing...' }
    }
    if (state === 'input-available') {
      return { icon: 'cog', color: 'text-info', label: 'Calling...' }
    }
    if (isError) {
      return { icon: 'times-circle', color: 'text-danger', label: 'Error' }
    }
    if (isResult) {
      return { icon: 'check-circle', color: 'text-success', label: 'Completed' }
    }
    return { icon: 'question-circle', color: 'text-muted', label: 'Unknown' }
  }

  const stateIndicator = getStateIndicator()

  // Get result if available (only in 'output-available' state)
  const result = isResult ? output : undefined

  if (rendererKey && isResult && result !== undefined) {
    const rendererProps = unwrapRendererProps(result)
    if (rendererProps) {
      return <Renderer {...rendererProps} />
    }
    // Structured data missing — fall through to the JSON fallback below so
    // the user at least sees the raw result instead of an empty renderer.
  }

  const hasDetails = !isEmpty(input as Record<string, unknown>) || (isResult && result !== undefined)

  return (
    <div className="p-2 rounded border bg-body-secondary">
      {/* Tool call header - clickable to expand/collapse */}
      <HorizontalStack
        gap={2}
        className={hasDetails ? styles.toolHeader : ''}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
        role={hasDetails ? 'button' : undefined}
      >
        {hasDetails && <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} className="text-muted" />}
        <Icon name="wrench" className="text-secondary" />
        <span className="fw-semibold">{humanize(toolName)}</span>
        {isProcessing ? (
          <Spinner animation="border" size="sm" className="ms-auto text-warning" />
        ) : (
          <span className={`ms-auto small ${stateIndicator.color}`}>
            <Icon name={stateIndicator.icon} className="me-1" />
            {stateIndicator.label}
          </span>
        )}
      </HorizontalStack>

      {/* Collapsible content */}
      <Collapse in={isExpanded}>
        <div>
          {/* Tool arguments */}
          {!isEmpty(input as Record<string, unknown>) && (
            <div className="mt-2">
              <div className="small text-muted mb-1">Arguments:</div>
              <ToolArguments args={input as Record<string, unknown>} />
            </div>
          )}

          {/* Tool result */}
          {isResult && result !== undefined && (
            <div className="mt-2">
              <div className="small text-muted mb-1">Result:</div>
              <ToolResult result={result} />
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}

/**
 * Renders tool arguments in a readable format
 */
const ToolArguments = ({ args }: { args: Record<string, unknown> }) => {
  return (
    <div className={`small p-2 bg-body rounded font-monospace ${styles.toolArguments}`}>
      {Object.entries(args).map(([key, value]) => (
        <div key={key} className="mb-1">
          <span className={styles.toolArgumentKey}>{key}:</span> <span className="text-body">{formatValue(value)}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Renders tool result in a readable format
 */
const ToolResult = ({ result }: { result: unknown }) => {
  if (typeof result === 'string') {
    return <div className={`small p-2 bg-body rounded ${styles.toolResult}`}>{result}</div>
  }

  return (
    <div className={`small p-2 bg-body rounded font-monospace ${styles.toolResultJson}`}>
      {JSON.stringify(result, null, 2)}
    </div>
  )
}

/**
 * Format a value for display
 */
const formatValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return `"${value}"`
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

const unwrapRendererProps = (result: unknown): Record<string, unknown> | null => {
  if (typeof result !== 'object' || result === null || Array.isArray(result)) {
    return null
  }
  const record = result as Record<string, unknown>
  // Post-2026-04-15 shape: `{ summary, data: { ... } }`. Unwrap `data` when
  // it's a plain object.
  if ('data' in record && typeof record.data === 'object' && record.data !== null && !Array.isArray(record.data)) {
    return record.data as Record<string, unknown>
  }
  // Legacy shape: the renderer props are already at the top level.
  return record
}
